import React, { useContext, useMemo, useState } from 'react';
import { Input, Segmented, Space, Tag, theme, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import AgingDashboardContext from '@totem/components/ticketing/agingDashboard/AgingDashboardContext';
import TicketCell from '@totem/components/ticketing/agingDashboard/TicketCell';
import {
  AgingTicket,
  TicketsDrill,
  TicketsView,
} from '@totem/components/ticketing/agingDashboard/types';
import {
  bucketOf,
  formatDate,
  formatDays,
  formatHours,
  inView,
  PRIORITY_TAG_COLORS,
} from '@totem/components/ticketing/agingDashboard/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

type Props = {
  drill: TicketsDrill;
  onDrillChange: (drill: TicketsDrill) => void;
};

const VIEWS: { value: TicketsView; label: string }[] = [
  { value: 'breached', label: 'SLA breached' },
  { value: 'stale7', label: 'No update 7+ days' },
  { value: 'awaitingReply', label: 'Awaiting first reply' },
  { value: 'followUpOverdue', label: 'Follow-up overdue' },
  { value: 'resolved', label: 'Resolved, awaiting close' },
  { value: 'all', label: 'All open' },
];

const UPDATED_BY_LABELS = {
  ib: 'Intelligent Buildings',
  customer: 'Customer',
  none: 'No reply yet',
};

const AgingTicketsTab = ({ drill, onDrillChange }: Props) => {
  const { loading, data, measure } = useContext(AgingDashboardContext);
  const { token } = theme.useToken();
  const [search, setSearch] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(20);

  const tickets = data?.tickets ?? [];
  const secondary = { color: token.colorTextSecondary, fontSize: 12 };

  const counts = useMemo(
    () =>
      Object.fromEntries(
        VIEWS.map((view) => [
          view.value,
          tickets.filter((ticket) => inView(ticket, view.value)).length,
        ]),
      ) as Record<TicketsView, number>,
    [tickets],
  );

  const bucketLabel = useMemo(() => {
    const buckets =
      measure === 'update' ? data?.sinceUpdate : data?.sinceOpened;
    return buckets?.find((bucket) => bucket.key === drill.bucketKey)?.label;
  }, [data, measure, drill.bucketKey]);
  const buildingLabel = data?.byBuilding.find(
    (group) => group.key === drill.buildingId,
  )?.label;

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return tickets
      .filter((ticket) => inView(ticket, drill.view))
      .filter(
        (ticket) =>
          !drill.bucketKey || bucketOf(ticket, measure) === drill.bucketKey,
      )
      .filter(
        (ticket) => !drill.buildingId || ticket.buildingId === drill.buildingId,
      )
      .filter(
        (ticket) => drill.type === undefined || ticket.type === drill.type,
      )
      .filter(
        (ticket) =>
          term === '' ||
          [ticket.number, ticket.summary, ticket.buildingName, ticket.category]
            .filter(Boolean)
            .some((text) => text.toLowerCase().includes(term)),
      );
  }, [tickets, drill, measure, search]);

  const sinceUpdateCell = (ticket: AgingTicket) => {
    const level =
      ticket.breached || ticket.staleDays >= 14
        ? 'error'
        : ticket.staleDays >= 7
          ? 'warning'
          : 'success';
    return (
      <span
        style={{
          display: 'inline-block',
          minWidth: 44,
          textAlign: 'center',
          padding: '1px 6px',
          borderRadius: 4,
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
          background:
            token[
              `color${level === 'error' ? 'Error' : level === 'warning' ? 'Warning' : 'Success'}Bg`
            ],
          color:
            token[
              `color${level === 'error' ? 'Error' : level === 'warning' ? 'Warning' : 'Success'}Text`
            ],
        }}
      >
        {ticket.pendingCustomer ? 'Customer' : formatDays(ticket.staleDays)}
      </span>
    );
  };

  const columns: ColumnProps<AgingTicket>[] = [
    {
      title: 'Ticket',
      key: 'number',
      render: (_, ticket: AgingTicket) => <TicketCell ticket={ticket} />,
      sorter: (compA, compB) => sortStringAscending(compA.number, compB.number),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: (compA, compB) => sortStringAscending(compA.type, compB.type),
    },
    {
      title: 'Priority',
      key: 'priority',
      render: (_, ticket) => (
        <Tag
          color={PRIORITY_TAG_COLORS[ticket.priority] ?? 'default'}
          style={{ marginInlineEnd: 0 }}
        >
          {ticket.priority || '-'}
        </Tag>
      ),
      sorter: (compA, compB) =>
        sortStringAscending(compA.priority, compB.priority),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, ticket) => (
        <>
          {ticket.status}
          {ticket.subStatus && <div style={secondary}>{ticket.subStatus}</div>}
        </>
      ),
    },
    {
      title: 'Building',
      dataIndex: 'buildingName',
      key: 'buildingName',
      sorter: (compA, compB) =>
        sortStringAscending(compA.buildingName, compB.buildingName),
    },
    {
      title: 'Age',
      key: 'age',
      align: 'right',
      render: (_, ticket) => formatDays(ticket.ageDays),
      sorter: (compA, compB) =>
        sortNumberAscending(compA.ageDays, compB.ageDays),
    },
    {
      title: 'Since update',
      key: 'stale',
      render: (_, ticket) => (
        <Space size={4} wrap>
          {sinceUpdateCell(ticket)}
          {ticket.breached && (
            <Tooltip
              title={`Open longer than the ${ticket.priority} SLA target (${formatHours(ticket.slaThresholdHours)})`}
            >
              <Tag color="error" style={{ marginInlineEnd: 0 }}>
                SLA breached
              </Tag>
            </Tooltip>
          )}
          {ticket.atRisk && (
            <Tag color="warning" style={{ marginInlineEnd: 0 }}>
              At risk
            </Tag>
          )}
        </Space>
      ),
      defaultSortOrder: 'descend',
      sorter: (compA, compB) =>
        sortNumberAscending(compA.staleDays, compB.staleDays),
    },
    {
      title: 'Last update by',
      key: 'lastUpdateBy',
      render: (_, ticket) =>
        ticket.lastUpdateBy === 'none' ? (
          <span style={secondary}>No reply yet</span>
        ) : (
          <>
            {UPDATED_BY_LABELS[ticket.lastUpdateBy]}
            {ticket.lastUpdateNote && (
              <Tooltip title={ticket.lastUpdateNote}>
                <div
                  style={{
                    ...secondary,
                    maxWidth: 260,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ticket.lastUpdateNote}
                </div>
              </Tooltip>
            )}
          </>
        ),
    },
    {
      title: 'Follow-up',
      key: 'followUp',
      render: (_, ticket) =>
        ticket.followUpAt ? (
          <span
            style={{
              color: ticket.followUpOverdue ? token.colorErrorText : undefined,
            }}
          >
            {formatDate(ticket.followUpAt)}
            {ticket.followUpOverdue ? ' · overdue' : ''}
          </span>
        ) : (
          <span style={secondary}>—</span>
        ),
      sorter: (compA, compB) =>
        sortStringAscending(compA.followUpAt ?? '', compB.followUpAt ?? ''),
    },
  ];

  if (data?.showQueues) {
    columns.push({
      title: (
        <Tooltip title="Visible to Intelligent Buildings users only">
          Queue <Tag color="purple">IB</Tag>
        </Tooltip>
      ),
      dataIndex: 'queue',
      key: 'queue',
      sorter: (compA, compB) => sortStringAscending(compA.queue, compB.queue),
    });
  }

  const drillChips = [
    drill.bucketKey &&
      bucketLabel && {
        key: 'bucket',
        label: `${measure === 'update' ? 'Since update' : 'Since opened'}: ${bucketLabel}`,
        clear: { ...drill, bucketKey: undefined },
      },
    drill.buildingId && {
      key: 'building',
      label: `Building: ${buildingLabel ?? 'selected'}`,
      clear: { ...drill, buildingId: undefined },
    },
    drill.type !== undefined && {
      key: 'type',
      label: `Type: ${drill.type || 'none'}`,
      clear: { ...drill, type: undefined },
    },
  ].filter(Boolean) as { key: string; label: string; clear: TicketsDrill }[];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        {/* Segmented doesn't wrap; scroll it sideways on narrow screens. */}
        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Segmented<TicketsView>
            value={drill.view}
            onChange={(view) => onDrillChange({ ...drill, view })}
            options={VIEWS.map((view) => ({
              value: view.value,
              label: `${view.label} ${counts[view.value] ?? 0}`,
            }))}
          />
        </div>
        <Input.Search
          allowClear
          placeholder="Search number, summary, building"
          style={{ width: 280 }}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {drillChips.length > 0 && (
        <Space size={6} wrap>
          <span style={secondary}>Filtered by:</span>
          {drillChips.map((chip) => (
            <Tag
              key={chip.key}
              closable
              onClose={() => onDrillChange(chip.clear)}
            >
              {chip.label}
            </Tag>
          ))}
        </Space>
      )}
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey={(ticket) => ticket.id}
        scroll={{ x: true }}
        pagination={{
          pageSize,
          showSizeChanger: true,
          onShowSizeChange: (_, size) => setPageSize(size),
          showTotal: (total) => `${total} tickets`,
        }}
      />
      {data && (
        <div style={secondary}>
          SLA targets:{' '}
          {data.slaThresholds
            .map(
              (threshold) =>
                `${threshold.priority} ${formatHours(threshold.thresholdHours)}`,
            )
            .join(' · ')}
          , measured from when the ticket was opened (projects and Optimize
          excluded). "Since update" counts only updates the customer can see.
        </div>
      )}
    </div>
  );
};

export default AgingTicketsTab;
